import { recordPath, recordRoute } from '@/shared/registry'

/** Where the API client pages live. */
export const apiClientsPath = '/channels/api-clients'
export const createApiClientPath = `${apiClientsPath}/new`
export const apiClientRoute = recordRoute('channels', 'api-clients')
export const editApiClientRoute = `${apiClientRoute}/edit`

/** The query parameter that presets the organization on Create API Client, as the organization's panel does. */
export const organizationParameter = 'organization'

/** An API client's details page. */
export function apiClientPath(id: string): string {
  return recordPath('channels', 'api-clients', id)
}

/** Create API Client for an organization. */
export function createApiClientFor(organizationId: string): string {
  return `${createApiClientPath}?${new URLSearchParams({ [organizationParameter]: organizationId }).toString()}`
}

/** The base of API Client Details' slots (`detailsSlots`), for other features' tabs and panels. */
export const apiClientDetailsSlot = 'channels.api-client-details'

/**
 * The API's Swagger UI, reached from a browser at the absolute path nginx and the dev server
 * proxy straight to the API, as Diagnostics links it.
 */
export const apiDocumentationHref = '/swagger/index.html'

/** The permissions the API client pages need. */
export const readApiClientsPermission = 'channels.api-clients.read'
export const writeApiClientsPermission = 'channels.api-clients.write'
export const deleteApiClientsPermission = 'channels.api-clients.delete'
export const serviceLevelPermission = 'channels.api-clients.service-level.write'
export const readCredentialsPermission = 'channels.api-clients.credentials.read'
export const writeCredentialsPermission = 'channels.api-clients.credentials.write'
export const readRoleAssignmentsPermission = 'access-control.role-assignments.read'
export const readManagedOrganizationsPermission = 'access-control.managed-organizations.read'

/** The API's API client endpoints. */
export const apiClientsApi = '/channels/v1/api-clients'
export const apiClientNamesApi = '/channels/v1/api-client-names'

export function apiClientApi(id: string): string {
  return `${apiClientsApi}/${encodeURIComponent(id)}`
}

/** A client's secrets and certificates, and one of them. */
export function credentialsApi(id: string): string {
  return `${apiClientApi(id)}/credentials`
}

export function credentialApi(id: string, credentialId: string): string {
  return `${credentialsApi(id)}/${encodeURIComponent(credentialId)}`
}

/** Where a client's new secret or certificate is posted. */
export function secretsApi(id: string): string {
  return `${apiClientApi(id)}/secrets`
}

export function certificatesApi(id: string): string {
  return `${apiClientApi(id)}/certificates`
}

/** A client's roles, held by Access Control: GET lists them, POST assigns one. */
export function apiClientRoleAssignmentsApi(id: string): string {
  return `${apiClientApi(id)}/role-assignments`
}

/** One of a client's role assignments: DELETE revokes it. */
export function apiClientRoleAssignmentApi(id: string, assignmentId: string): string {
  return `${apiClientRoleAssignmentsApi(id)}/${encodeURIComponent(assignmentId)}`
}

/** The organizations a client manages, held by Access Control: GET lists them, PUT sets them. */
export function apiClientManagedOrganizationsApi(id: string): string {
  return `${apiClientApi(id)}/managed-organizations`
}

/** An organization's API clients, a page at a time: disabled ones and those being deleted too. */
export function organizationApiClientsApi(organizationId: string): string {
  return `${apiClientsApi}?${new URLSearchParams({ organizationId, includeInactive: 'true' }).toString()}`
}

/** Site Management's organization, for its name when Create API Client is opened for one. */
export function organizationApi(organizationId: string): string {
  return `/site-management/v1/organizations/${encodeURIComponent(organizationId)}`
}
